import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Calendar, FileText, Clock, Search, ArrowLeft, Eye, ExternalLink, Lock
} from 'lucide-react';
import { format } from 'date-fns';
import { documentDeadline } from '../utils/documentDeadline';
import { es } from 'date-fns/locale';
import { getPeriodDateRange, getPeriodStartMonth, parseIntervalMonths } from '../utils/periodUtils';
import { fetchOverridesForUser, getOverrideLabel, UserRequirementOverride } from '../utils/overrideUtils';
import { DisplayStatus, STATUS_META, TONE_CLASSES, statusBadgeClass } from '../utils/statusMeta';
import { formatPersonName } from '../utils/formatName';
import { PageContainer, PageHeader } from '../components/layout/Page';
import AttentionPanel from '../components/supplier/AttentionPanel';
import UploadDropzone, { acceptsSuaFiles } from '../components/supplier/UploadDropzone';
import PreviewDialog from '../components/supplier/PreviewDialog';
import Toast, { useToast } from '../components/supplier/Toast';
import useMediaQuery from '../components/supplier/useMediaQuery';
import { getDeadlineChip } from '../components/supplier/deadlineChip';
import {
  MONTH_CODES, MONTH_NAMES, MONTH_SHORT, REVISION_PREFIX, STATUS_SYMBOL,
  buildAttentionItems, cellDisplayStatus, documentDisplayStatus, getUploadAvailability,
  latestActiveDocument, reviewerReason,
} from '../components/supplier/supplierStatus';

interface Requirement {
  id: string;
  user_id: string;
  definition_id: string;
  status: string;
  due_date: string | null;
  assigned_at: string;
  definition: {
    name: string;
    description: string | null;
    category_id: string | null;
    renewal_type: string;
    renewal_interval: string | null;
    is_moral: boolean | null;
    category?: {
      name: string;
    } | null;
  };
  documents?: Array<{
    id: string;
    file_location: string;
    original_filename: string;
    upload_date: string;
    status: string;
    approval_timestamp: string | null;
    rejection_reason: string | null;
    issue_date: string | null;
    expiry_date: string | null;
    applicable_month?: string | null;
    applicable_year?: number | null;
    is_deleted?: boolean | null;
  }> | null;
}

type RequirementDocument = NonNullable<Requirement['documents']>[number];

interface AnnualCell {
  key: string;
  span: number;
  clickMonth: string;
  isClickable: boolean;
  status: DisplayStatus;
  titleText: string;
  documentId?: string;
  shortLabel: string;
}

const STATUS_FILTERS: DisplayStatus[] = ['missing', 'overdue', 'pending_approval', 'correction_requested', 'rejected', 'approved'];

/** Supplier-facing explanation of an upload failure; technical details go to the console. */
function uploadErrorMessage(error: unknown): string {
  const base = 'No se pudo subir el archivo. ';
  const raw = error && typeof error === 'object' && 'message' in error ? String((error as { message: unknown }).message) : '';
  const message = raw.toLowerCase();
  if (message.includes('storage') || message.includes('bucket')) return base + 'Verifica que el archivo no esté dañado e inténtalo de nuevo.';
  if (message.includes('network') || message.includes('fetch')) return base + 'Revisa tu conexión a internet e inténtalo de nuevo.';
  if (message.includes('timeout')) return base + 'La carga tardó demasiado. Inténtalo de nuevo con una conexión más estable.';
  if (message.includes('size') || message.includes('large')) return base + 'El archivo es demasiado grande o está dañado.';
  if (message.includes('permission') || message.includes('unauthorized')) return base + 'No tienes permisos para subir este archivo. Contacta a tu administrador.';
  if (message.includes('database') || message.includes('insert')) return base + 'El archivo se recibió, pero no se pudo registrar. Contacta a tu administrador antes de volver a intentarlo.';
  return base + 'Inténtalo de nuevo; si el problema continúa, contacta a tu administrador.';
}

export default function UserDashboard() {
  const { user, userProfile } = useAuth();
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string | null>>({});
  const [fileMessages, setFileMessages] = useState<Record<string, { tone: 'error' | 'info'; text: string; url?: string } | null>>({});
  const { toast: toastMessage, showToast, dismiss: dismissToast } = useToast();
  const isSmUp = useMediaQuery('(min-width: 640px)');
  // Synchronous guard against double-submit: React state updates too late to
  // block a second upload fired before the next render.
  const uploadsInFlight = useRef<Set<string>>(new Set());
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1);
    return previousMonth.getMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1);
    return previousMonth.getFullYear();
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [calendarOffset, setCalendarOffset] = useState(0);
  const [allMonthlyDocumentDefinitions, setAllMonthlyDocumentDefinitions] = useState<any[]>([]);
  const [allRequirementsForTable, setAllRequirementsForTable] = useState<Requirement[]>([]);
  const [scrollToDefinitionId, setScrollToDefinitionId] = useState<string | null>(null);
  const [scrollToDocumentId, setScrollToDocumentId] = useState<string | null>(null);
  const [pendingScroll, setPendingScroll] = useState<{month: number, year: number, definitionId: string, documentId?: string} | null>(null);

  // Dev mode: User impersonation
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
  const [impersonatedUserId, setImpersonatedUserId] = useState<string>('');
  const [showDevMenu, setShowDevMenu] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{id: string, email: string, full_name: string | null}>>([]);

  // PDF Preview Modal
  const [preview, setPreview] = useState<{ id: string; name: string } | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Override user ID if impersonating in dev mode
  const effectiveUserId = isDevMode && impersonatedUserId ? impersonatedUserId : user?.id;
  const [userOverrides, setUserOverrides] = useState<UserRequirementOverride[]>([]);

  useEffect(() => {
    if (effectiveUserId) {
      fetchOverridesForUser(effectiveUserId).then(setUserOverrides);
    } else {
      setUserOverrides([]);
    }
  }, [effectiveUserId]);

  // Fetch available users for impersonation in dev mode
  useEffect(() => {
    if (isDevMode) {
      const fetchUsers = async () => {
        const { data, error } = await supabase
          .from('users')
          .select('id, email, full_name')
          .eq('role', 'normal')
          .eq('is_active', true)
          .eq('is_deleted', false)
          .order('email', { ascending: true })
          .limit(50);

        if (!error && data) {
          setAvailableUsers(data);
        }
      };
      fetchUsers();
    }
  }, [isDevMode]);

  useEffect(() => {
    if (user && userProfile) {
      fetchUserRequirements();
      fetchAllMonthlyDocumentDefinitions();
      fetchAllRequirementsForTable();
    }
  }, [user, userProfile, selectedMonth, selectedYear, effectiveUserId]);

  // Handle pending scroll after month change
  useEffect(() => {
    if (pendingScroll && !loading && requirements.length > 0) {
      // Check if we're on the right month
      if (pendingScroll.month === selectedMonth && pendingScroll.year === selectedYear) {
        setScrollToDefinitionId(pendingScroll.definitionId);
        setScrollToDocumentId(pendingScroll.documentId || null);
        setPendingScroll(null);
      }
    }
  }, [pendingScroll, loading, requirements, selectedMonth, selectedYear]);

  // Handle scrolling after data updates
  useEffect(() => {
    if (scrollToDefinitionId && viewMode === 'list' && requirements.length > 0) {
      setTimeout(() => {
        // Find the requirement with this definition_id in the current month's requirements
        let requirement = requirements.find(req => req.definition_id === scrollToDefinitionId);

        // If not found in current month, try to find in all requirements (table view uses all requirements)
        if (!requirement && allRequirementsForTable.length > 0) {
          requirement = allRequirementsForTable.find(req => req.definition_id === scrollToDefinitionId);
        }

        if (requirement) {
          let targetElement: HTMLElement | null = null;
          let targetId = '';

          // If documentId is provided, try to scroll to the specific document first
          if (scrollToDocumentId) {
            targetId = `document-${scrollToDocumentId}`;
            targetElement = document.getElementById(targetId);
          }

          // If no document element found, scroll to the requirement card
          if (!targetElement) {
            targetId = `requirement-${requirement.id}`;
            targetElement = document.getElementById(targetId);
          }

          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            targetElement.classList.add('ring-2', 'ring-blue-500');
            setTimeout(() => {
              targetElement!.classList.remove('ring-2', 'ring-blue-500');
            }, 2000);
          }
        }
        setScrollToDefinitionId(null);
        setScrollToDocumentId(null);
      }, 300);
    }
  }, [scrollToDefinitionId, scrollToDocumentId, viewMode, requirements, allRequirementsForTable]);

  const fetchAllMonthlyDocumentDefinitions = async () => {
    try {
      const { data: definitions, error } = await supabase
        .from('document_definitions')
        .select(`
          id,
          name,
          description,
          category_id,
          renewal_type,
          renewal_interval,
          is_moral,
          category:categories(name)
        `)
        .order('name', { ascending: true });

      if (error) throw error;
      
      const filteredDefinitions = (definitions || []).filter((def: any) => {
        if (def.is_moral === null || def.is_moral === undefined) {
          return true;
        }
        return def.is_moral === userProfile?.is_moral;
      });
      
      setAllMonthlyDocumentDefinitions(filteredDefinitions);
    } catch (error) {
      console.error('Error fetching all monthly document definitions:', error);
    }
  };

  const fetchAllRequirementsForTable = async () => {
    try {
      const query = supabase
        .from('user_document_requirements')
        .select(`
          *,
          definition:document_definitions(
            *,
            category:categories(name)
          ),
          documents:documents!documents_requirement_id_fkey(*)
        `)
        .eq('user_id', effectiveUserId!)
        .order('due_date', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;
      
      const filteredData = (data || []).filter((req: any) => {
        if (req.definition?.is_moral === null || req.definition?.is_moral === undefined) {
          return true;
        }
        return req.definition.is_moral === userProfile?.is_moral;
      });
      
      setAllRequirementsForTable(filteredData as Requirement[]);
    } catch (error) {
      console.error('Error fetching all requirements for table:', error);
    }
  };

  const fetchUserRequirements = async () => {
    try {
      setLoading(true);
      
      // 1. Get ALL document definitions (not requirements)
      const { data: definitions, error: defError } = await supabase
        .from('document_definitions')
        .select(`
          *,
          category:categories(name)
        `)
        .order('name', { ascending: true });

      if (defError) throw defError;

      // Filter definitions based on user's is_moral status
      const filteredByMoral = (definitions || []).filter((def: any) => {
        if (def.is_moral === null || def.is_moral === undefined) {
          return true;
        }
        return def.is_moral === userProfile?.is_moral;
      });

      // 2. VISTA VERTICAL: Mostrar TODAS las definiciones, sin filtrar por mes
      // El filtrado por mes se aplica solo a los documentos dentro de cada definición
      const definitionsForMonth = filteredByMoral;


      // 3. Para cada definición, buscar requirement específico del período actual
      const requirementsWithData = await Promise.all(
        definitionsForMonth.map(async (def: any) => {
          // Calcular el rango de fechas para buscar el requirement del período correcto
          const year = selectedYear;
          const month1Based = selectedMonth + 1; // Convert to 1-based month

          // Determine the effective renewal type (handle aliases like '1 mons' for monthly, '12 mons' for yearly)
          let effectiveRenewalType = def.renewal_type;
          if (def.renewal_interval === '1 mons') effectiveRenewalType = 'monthly';
          if (def.renewal_interval === '12 mons') effectiveRenewalType = 'yearly';

          // Get period start month for custom intervals
          const periodMonth = effectiveRenewalType === 'custom_interval'
            ? getPeriodStartMonth(month1Based, parseIntervalMonths(def.renewal_interval))
            : month1Based;

          const { startDate: periodStart, endDate: periodEnd } = getPeriodDateRange(
            effectiveRenewalType,
            def.renewal_interval,
            year,
            periodMonth
          );

          const startDate = format(periodStart, 'yyyy-MM-dd');
          const endDate = format(periodEnd, 'yyyy-MM-dd');

          // Buscar requirement del período específico
          let requirement: any = null;
          let documents: any[] = [];

          const { data: reqData, error: reqError } = await supabase
            .from('user_document_requirements')
            .select(`
              *,
              documents:documents!documents_requirement_id_fkey(*)
            `)
            .eq('user_id', effectiveUserId!)
            .eq('definition_id', def.id)
            .gte('due_date', startDate)
            .lte('due_date', endDate)
            .order('assigned_at', { ascending: true });

          // Oldest requirement represents the period; duplicates may exist from
          // past double-submits, so merge their documents to keep them visible
          if (!reqError && reqData && reqData.length > 0) {
            requirement = reqData[0];

            // Helper function to convert month enum to number
            const monthToNumber = (monthStr: string): number => {
              const months: { [key: string]: number } = {
                'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4,
                'MAY': 5, 'JUN': 6, 'JUL': 7, 'AUG': 8,
                'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
              };
              return months[monthStr] || 0;
            };

            // Filter documents by applicable_month based on the current period
            const allDocuments = reqData.flatMap((req: any) => req.documents || []);
            documents = allDocuments.filter((doc: any) => {
              // Always include deleted documents for history
              if (doc.is_deleted) return true;

              // VISTA VERTICAL: Solo mostrar documentos CON applicable_month del período correcto
              // Si no tiene applicable_month/year, NO incluir (para evitar duplicaciones)
              if (!doc.applicable_month || !doc.applicable_year) return false;

              // Check if document's year matches
              if (doc.applicable_year !== selectedYear) return false;

              const docMonth = monthToNumber(doc.applicable_month);
              if (!docMonth) return false;

              // Filter based on renewal type/interval
              if (def.renewal_type === 'monthly' || def.renewal_interval === '1 mons') {
                // Monthly: only show documents for the exact selected month
                return docMonth === (selectedMonth + 1);
              }
              else if (def.renewal_type === 'yearly' || def.renewal_interval === '12 mons') {
                // Yearly: show documents from any month in the year
                return true;
              }
              else if (def.renewal_type === 'custom_interval') {
                const interval = def.renewal_interval;

                if (interval === '2 mons' || interval === '2 months') {
                  // Bimestral: JAN-FEB, MAR-APR, MAY-JUN, JUL-AUG, SEP-OCT, NOV-DEC
                  const periodStart = Math.floor(selectedMonth / 2) * 2;
                  const periodEnd = periodStart + 1;
                  return docMonth >= (periodStart + 1) && docMonth <= (periodEnd + 1);
                }
                else if (interval === '3 mons' || interval === '3 months') {
                  // Trimestral: JAN-MAR, APR-JUN, JUL-SEP, OCT-DEC
                  const periodStart = Math.floor(selectedMonth / 3) * 3;
                  const periodEnd = periodStart + 2;
                  return docMonth >= (periodStart + 1) && docMonth <= (periodEnd + 1);
                }
                else if (interval === '4 mons' || interval === '4 months') {
                  // Cuatrimestral: JAN-APR, MAY-AUG, SEP-DEC
                  if (selectedMonth >= 0 && selectedMonth <= 3) {
                    return docMonth >= 1 && docMonth <= 4; // JAN-APR
                  } else if (selectedMonth >= 4 && selectedMonth <= 7) {
                    return docMonth >= 5 && docMonth <= 8; // MAY-AUG
                  } else {
                    return docMonth >= 9 && docMonth <= 12; // SEP-DEC
                  }
                }
                else if (interval === '6 mons' || interval === '6 months') {
                  // Semestral: JAN-JUN, JUL-DEC
                  if (selectedMonth >= 0 && selectedMonth <= 5) {
                    return docMonth >= 1 && docMonth <= 6; // JAN-JUN
                  } else {
                    return docMonth >= 7 && docMonth <= 12; // JUL-DEC
                  }
                }
              }

              // Default: NO incluir el documento si no coincide con ningún filtro
              return false;
            });
          }
          
          // Return either the real requirement or a virtual one
          if (requirement) {
            return {
              ...requirement,
              definition: def,
              documents
            };
          } else {
            return {
              id: `def-${def.id}`,
              user_id: effectiveUserId!,
              definition_id: def.id,
              status: 'required',
              due_date: null,
              assigned_at: new Date().toISOString(),
              definition: def,
              documents: []
            };
          }
        })
      );
      
      setRequirements(requirementsWithData as Requirement[]);
    } catch (error) {
      console.error('Error fetching requirements:', error);
    } finally {
      setLoading(false);
    }
  };

  const setUploadError = (requirementId: string, message: string | null) =>
    setUploadErrors(prev => ({ ...prev, [requirementId]: message }));

  const handleFileUpload = async (requirementId: string, file: File) => {
    if (uploadsInFlight.current.has(requirementId)) {
      console.warn('Subida ya en progreso para este requerimiento, ignorando intento duplicado');
      return;
    }
    uploadsInFlight.current.add(requirementId);
    setUploadingFiles(prev => ({ ...prev, [requirementId]: true }));
    setUploadError(requirementId, null);

    try {
      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        setUploadError(requirementId, `El archivo es demasiado grande (${(file.size / 1024 / 1024).toFixed(2)} MB). El máximo permitido es 50 MB.`);
        return;
      }

      // Get requirement details to check document name.
      // Use the original requirementId: for new periods the requirement only
      // exists in local state under its virtual "def-" id.
      const currentRequirement = requirements.find(r => r.id === requirementId);
      const documentName = currentRequirement?.definition?.name || '';
      const isSuaDocument = acceptsSuaFiles(documentName);

      // Validate file type - PDF always allowed, SUA only for "Archivo .SUA"
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = isSuaDocument ? ['pdf', 'sua'] : ['pdf'];

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        if (fileExtension === 'sua' && !isSuaDocument) {
          setUploadError(requirementId, 'Los archivos .SUA solo se aceptan en el documento "Archivo .SUA". Aquí solo se aceptan archivos PDF.');
        } else {
          const detected = fileExtension && fileExtension !== file.name.toLowerCase() ? `.${fileExtension}` : 'sin extensión';
          setUploadError(requirementId, `Este documento solo acepta archivos ${allowedExtensions.map(ext => ext.toUpperCase()).join(' o ')}. El archivo seleccionado es ${detected}.`);
        }
        return;
      }

      // Validate filename length after sanitization
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      if (sanitizedFileName.length > 200) {
        setUploadError(requirementId, `El nombre del archivo es demasiado largo (${sanitizedFileName.length} caracteres). Renómbralo con un máximo de 200 caracteres.`);
        return;
      }

      // Check if we need to create a requirement first (for def-XXX ids)
      let actualRequirementId = requirementId;

      if (requirementId.startsWith('def-')) {
        const definitionId = requirementId.replace('def-', '');

        // Find the definition to get its details
        const definition = requirements.find(r => r.id === requirementId)?.definition;
        if (!definition) {
          throw new Error('No se encontró la definición');
        }

        // Calculate due date based on definition type and current month
        let dueDate: string;
        const year = selectedYear;
        const month = selectedMonth; // This is 0-11
        const monthForDate = month + 1; // Convert to 1-12 for date string

        // Helper function to get last day of month
        const getLastDayOfMonth = (year: number, month: number): number => {
          // month here should be 0-11 for JavaScript Date
          return new Date(year, month + 1, 0).getDate();
        };

        if (definition.renewal_type === 'monthly' || definition.renewal_interval === '1 mons') {
          // Monthly: due at end of current month
          const lastDay = getLastDayOfMonth(year, month);
          dueDate = `${year}-${monthForDate.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
        }
        else if (definition.renewal_type === 'yearly' || definition.renewal_interval === '12 mons') {
          // Yearly: always due December 31st
          dueDate = `${year}-12-31`;
        }
        else if (definition.renewal_type === 'custom_interval') {
          const interval = definition.renewal_interval;

          if (interval === '2 mons') {
            // Bimestral: determinar el periodo y vence al final del periodo de 2 meses
            const periodStart = Math.floor(month / 2) * 2; // 0,2,4,6,8,10
            const periodEnd = periodStart + 1; // Final del periodo bimestral
            const endYear = periodEnd > 11 ? year + 1 : year;
            const endMonthAdjusted = periodEnd > 11 ? periodEnd - 12 : periodEnd;
            const lastDay = getLastDayOfMonth(endYear, endMonthAdjusted);
            dueDate = `${endYear}-${(endMonthAdjusted + 1).toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
          }
          else if (interval === '4 mons') {
            // Cuatrimestral: determinar el periodo y vence al final del periodo de 4 meses
            const periodStart = Math.floor(month / 4) * 4; // 0,4,8
            const periodEnd = periodStart + 3; // Final del periodo cuatrimestral
            const endYear = periodEnd > 11 ? year + 1 : year;
            const endMonthAdjusted = periodEnd > 11 ? periodEnd - 12 : periodEnd;
            const lastDay = getLastDayOfMonth(endYear, endMonthAdjusted);
            dueDate = `${endYear}-${(endMonthAdjusted + 1).toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
          }
          else if (interval === '3 mons') {
            // Trimestral: determinar el periodo y vence al final del periodo de 3 meses
            const periodStart = Math.floor(month / 3) * 3; // 0,3,6,9
            const periodEnd = periodStart + 2; // Final del periodo trimestral
            const endYear = periodEnd > 11 ? year + 1 : year;
            const endMonthAdjusted = periodEnd > 11 ? periodEnd - 12 : periodEnd;
            const lastDay = getLastDayOfMonth(endYear, endMonthAdjusted);
            dueDate = `${endYear}-${(endMonthAdjusted + 1).toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
          }
          else if (interval === '6 mons') {
            // Semestral: determinar el periodo y vence al final del periodo de 6 meses
            const periodStart = Math.floor(month / 6) * 6; // 0,6
            const periodEnd = periodStart + 5; // Final del periodo semestral
            const endYear = periodEnd > 11 ? year + 1 : year;
            const endMonthAdjusted = periodEnd > 11 ? periodEnd - 12 : periodEnd;
            const lastDay = getLastDayOfMonth(endYear, endMonthAdjusted);
            dueDate = `${endYear}-${(endMonthAdjusted + 1).toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
          }
          else {
            // Default to end of current month for any other interval
            const lastDay = getLastDayOfMonth(year, month);
            dueDate = `${year}-${monthForDate.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
          }
        }
        else {
          // Default: end of current month
          const lastDay = getLastDayOfMonth(year, month);
          dueDate = `${year}-${monthForDate.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
        }

        // Always set is_current to false when creating a requirement
        const isCurrent = false;

        // Reuse an existing requirement for this period if one already exists
        // (created by a previous attempt, another tab, or an admin) to avoid duplicates
        const { data: existingReqs, error: findError } = await supabase
          .from('user_document_requirements')
          .select('id')
          .eq('user_id', effectiveUserId!)
          .eq('definition_id', definitionId)
          .eq('due_date', dueDate)
          .order('assigned_at', { ascending: true })
          .limit(1);

        if (findError) {
          console.error('Error buscando requerimiento existente:', findError);
          throw new Error('Error al verificar el requerimiento');
        }

        if (existingReqs && existingReqs.length > 0) {
          actualRequirementId = existingReqs[0].id;
        } else {
          // Create the requirement
          const { data: newRequirement, error: createError } = await supabase
            .from('user_document_requirements')
            .insert({
              user_id: effectiveUserId!,
              definition_id: definitionId,
              status: 'required',
              due_date: dueDate,
              assigned_at: new Date().toISOString(),
              is_current: isCurrent
            })
            .select()
            .single();

          if (createError || !newRequirement) {
            console.error('Error creando requerimiento:', createError);
            throw new Error('Error al crear el requerimiento');
          }

          actualRequirementId = newRequirement.id;
        }
      }

      // MIME type handling
      let fileToUpload: File | Blob = file;
      let finalMimeType = file.type;

      if (fileExtension === 'sua') {
        if (!file.type || file.type === '') {
          finalMimeType = 'application/octet-stream';
          fileToUpload = new Blob([file], { type: finalMimeType });
        } else {
          // Aceptar cualquier tipo MIME para archivos SUA, pero normalizar algunos comunes
          if (file.type === 'application/octet-stream' ||
              file.type === 'application/x-sua' ||
              file.type === 'text/plain' ||
              file.type.startsWith('application/')) {
            finalMimeType = 'application/octet-stream';
            fileToUpload = new Blob([file], { type: finalMimeType });
          }
        }
      }

      // Upload file to storage - use actual requirement ID
      const fileName = `${effectiveUserId!}/${actualRequirementId}/${Date.now()}_${sanitizedFileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, fileToUpload);

      if (uploadError) {
        console.error('Error en subida a storage:', uploadError);
        throw uploadError;
      }

      // Every renewal type marks the document with the selected month; the
      // period filters decide which documents a period shows.
      const monthEnumValues = [
        'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
        'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
      ] as const;
      const applicableYear = selectedYear;
      const applicableMonth: typeof monthEnumValues[number] = monthEnumValues[selectedMonth];

      // Create document record
      const { data: newDocument, error: insertError } = await supabase
        .from('documents')
        .insert({
          requirement_id: actualRequirementId,
          file_location: fileName,
          original_filename: file.name,
          mime_type: finalMimeType,
          file_size_bytes: file.size,
          applicable_month: applicableMonth,
          applicable_year: applicableYear
        })
        .select()
        .single();

      if (insertError || !newDocument) {
        console.error('Error al insertar documento en base de datos:', insertError);
        throw insertError || new Error('No se pudo crear el documento');
      }

      // Update the requirement with latest_document_id
      const { error: updateError } = await supabase
        .from('user_document_requirements')
        .update({
          latest_document_id: newDocument.id,
          status: 'fulfilled'
        })
        .eq('id', actualRequirementId);

      if (updateError) {
        console.error('Error actualizando requerimiento:', updateError);
        // Don't throw here, the document was uploaded successfully
      }

      showToast(`Recibimos "${file.name}". Quedó En revisión hasta que un revisor lo valide.`, 'success');

      // Refresh requirements after successful upload
      await fetchUserRequirements();
    } catch (error) {
      console.error('Error en subida de archivo:', error);
      setUploadError(requirementId, uploadErrorMessage(error));
    } finally {
      uploadsInFlight.current.delete(requirementId);
      setUploadingFiles(prev => ({ ...prev, [requirementId]: false }));
    }
  };

  const canOpenFiles = () => {
    if (effectiveUserId !== user?.id && userProfile?.role !== 'admin' && userProfile?.role !== 'reviewer') {
      showToast('La vista de prueba no cambia tus permisos. Para abrir archivos de otra persona, inicia sesión con una cuenta de administrador o revisor.', 'info');
      return false;
    }
    return true;
  };

  const handlePreview = async (doc: { id: string; file_location: string; original_filename: string }) => {
    if (!canOpenFiles()) return;
    // Check if file is PDF based on file location extension
    if (!doc.file_location.toLowerCase().endsWith('.pdf')) {
      showToast('La vista previa solo está disponible para archivos PDF.', 'info');
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(doc.file_location);

      if (error || !data) throw error || new Error('Archivo no disponible');

      // Create blob URL for PDF preview
      const url = URL.createObjectURL(data);
      setPdfUrl(url);
      setPreview({ id: doc.id, name: doc.original_filename });
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      showToast('No se pudo cargar la vista previa. Inténtalo de nuevo en unos minutos.', 'error');
    }
  };

  // Non-PDF files (e.g. SUA) open through a short-lived signed URL. The window
  // is opened synchronously on click so popup blockers allow it.
  const handleOpenFile = async (doc: { id: string; file_location: string }) => {
    if (!canOpenFiles()) return;
    setFileMessages(prev => ({ ...prev, [doc.id]: null }));
    const opened = window.open('', '_blank');
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.file_location, 60);
      if (error || !data?.signedUrl) throw error || new Error('Sin URL firmada');
      if (opened) {
        opened.opener = null;
        opened.location.href = data.signedUrl;
      } else {
        setFileMessages(prev => ({ ...prev, [doc.id]: { tone: 'info', text: 'Tu navegador bloqueó la ventana nueva.', url: data.signedUrl } }));
      }
    } catch (error) {
      console.error('Error opening document:', error);
      opened?.close();
      setFileMessages(prev => ({ ...prev, [doc.id]: { tone: 'error', text: 'No se pudo abrir el archivo. Inténtalo de nuevo en unos minutos.' } }));
    }
  };

  const closePreview = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setPreview(null);
  };

  // Generate monthly table data (similar to UserDocumentDetail)
  const generateMonthlyTableData = () => {
    // Generate 12 months for the current year based on offset
    const months: string[] = [];
    const currentYear = new Date().getFullYear() + calendarOffset;

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, i, 1);
      months.push(format(date, 'yyyy-MM'));
    }

    const monthlyStatuses: any[] = [];

    // Group documents by definition - using ALL requirements for the table
    const documentsByDefinition = new Map<string, any[]>();
    allRequirementsForTable.forEach(req => {
      if (req.documents && req.documents.length > 0) {
        const defName = req.definition.name;
        if (!documentsByDefinition.has(defName)) {
          documentsByDefinition.set(defName, []);
        }
        // Filter out deleted documents
        const activeDocuments = req.documents.filter((doc: any) => !doc.is_deleted);
        documentsByDefinition.get(defName)!.push(...activeDocuments);
      }
    });
    
    // Process all document definitions
    allMonthlyDocumentDefinitions.forEach((definition) => {
      const monthStatuses: Record<string, any> = {};
      
      // Initialize all months as not_uploaded
      months.forEach(month => {
        monthStatuses[month] = { status: 'not_uploaded' };
      });
      
      // Get documents for this definition
      const definitionDocs = documentsByDefinition.get(definition.name) || [];

      // Update status based on documents
      definitionDocs.forEach(doc => {
        let documentMonth: string;

        // Use applicable_month/year if available, otherwise fall back to upload_date
        if (doc.applicable_month && doc.applicable_year) {
          const monthNum = monthAbbrevToNumber(doc.applicable_month);
          if (monthNum) {
            documentMonth = `${doc.applicable_year}-${monthNum.toString().padStart(2, '0')}`;
          } else {
            documentMonth = format(new Date(doc.upload_date), 'yyyy-MM');
          }
        } else {
          documentMonth = format(new Date(doc.upload_date), 'yyyy-MM');
        }

        if (monthStatuses[documentMonth]) {
          const currentMonthStatus = monthStatuses[documentMonth];

          // Priority logic: approved > pending_approval > rejected > reemplazado
          const statusPriority: Record<string, number> = { 'approved': 4, 'pending_approval': 2, 'rejected': 1, 'reemplazado': 0, 'not_uploaded': 0 };
          const currentPriority = statusPriority[currentMonthStatus.status] || 0;
          const newPriority = statusPriority[doc.status] || 0;

          // Update if: no document yet, OR new document has better status, OR same status but newer
          if (!currentMonthStatus.uploadDate ||
              newPriority > currentPriority ||
              (newPriority === currentPriority && new Date(doc.upload_date) > new Date(currentMonthStatus.uploadDate))) {
            monthStatuses[documentMonth] = {
              status: doc.status,
              documentId: doc.id,
              uploadDate: doc.upload_date,
              rejectionReason: doc.rejection_reason
            };
          }
        }
      });
      
      // Find the user's requirement for this definition - from ALL requirements
      const userRequirement = allRequirementsForTable.find(req => req.definition_id === definition.id);
      
      monthlyStatuses.push({
        requirementId: userRequirement?.id || definition.id,  // Use user's requirement ID if exists
        definitionId: definition.id,
        documentName: definition.name,
        categoryName: definition.category?.name || 'Sin categoría',
        renewalType: definition.renewal_type,
        renewalInterval: definition.renewal_interval,
        dueDate: userRequirement?.due_date || null,
        monthStatuses
      });
    });
    
    // Sort by category name first, then by document name
    monthlyStatuses.sort((a, b) => {
      const categoryCompare = a.categoryName.localeCompare(b.categoryName);
      if (categoryCompare !== 0) return categoryCompare;
      return a.documentName.localeCompare(b.documentName);
    });

    return { monthlyStatuses, months };
  };
  
  const monthAbbrevToNumber = (monthAbbrev: string | null): number | null => {
    if (!monthAbbrev) return null;
    const months: Record<string, number> = {
      'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4,
      'MAY': 5, 'JUN': 6, 'JUL': 7, 'AUG': 8,
      'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
    };
    return months[monthAbbrev] || null;
  };

  const handleTableCellClick = (month: string, definitionId?: string, documentId?: string) => {
    const [year, monthNum] = month.split('-');
    const newYear = parseInt(year);
    const newMonth = parseInt(monthNum) - 1;

    // Check if we're changing to a different month
    const isChangingMonth = newYear !== selectedYear || newMonth !== selectedMonth;

    if (definitionId && isChangingMonth) {
      // Store the scroll info for after the month change loads
      setPendingScroll({ month: newMonth, year: newYear, definitionId, documentId });
    }

    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
    setViewMode('list');

    if (definitionId && !isChangingMonth) {
      // Same month, need to delay scroll to ensure view mode changes first
      setTimeout(() => {
        setScrollToDefinitionId(definitionId);
        setScrollToDocumentId(documentId || null);
      }, 100);
    }
  };

  // Annual grid cells for one document row: merges non-monthly periods into
  // one cell and decides which cells can be opened.
  const buildAnnualCells = (docStatus: any): AnnualCell[] => {
    const months = Object.keys(docStatus.monthStatuses).sort();
    const renewalType = docStatus.renewalType;
    const renewalInterval = docStatus.renewalInterval;

    // Calculate cell spans for non-monthly documents
    const getCellSpans = () => {
      const spans: Array<{startIndex: number, span: number, status: any, periodStartMonth?: string, periodEndMonth?: string}> = [];

      // PRIMERO: Determinar la estructura de las celdas basada en renewal_type
      if (!renewalType || renewalType === 'monthly' || renewalType === 'none') {
        // Para documentos mensuales o sin tipo, cada mes es una celda separada
        months.forEach((month, index) => {
          spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
        });
      } else if (renewalType === 'yearly') {
        // Para documentos anuales, fusionar los 12 meses del año
        // Agrupar meses por año
        const yearGroups: {[year: string]: {start: number, end: number}} = {};

        months.forEach((month, index) => {
          const [yearStr] = month.split('-');
          if (!yearGroups[yearStr]) {
            yearGroups[yearStr] = {start: index, end: index};
          } else {
            yearGroups[yearStr].end = index;
          }
        });

        // Crear spans para cada año visible
        Object.entries(yearGroups).forEach(([year, indices]) => {
          const yearMonths = months.slice(indices.start, indices.end + 1);
          // Get the best status in the year (approved > pending_approval > rejected > reemplazado > not_uploaded)
          const statusPriority: Record<string, number> = { 'approved': 4, 'pending_approval': 2, 'rejected': 1, 'reemplazado': 0, 'not_uploaded': 0 };
          let bestStatus = docStatus.monthStatuses[yearMonths[0]];
          yearMonths.forEach(month => {
            const monthStatus = docStatus.monthStatuses[month];
            if (statusPriority[monthStatus.status] > statusPriority[bestStatus.status]) {
              bestStatus = monthStatus;
            }
          });
          const yearStatus = bestStatus;

          spans.push({
            startIndex: indices.start,
            span: indices.end - indices.start + 1,
            status: yearStatus,
            periodStartMonth: `${year}-01` // Enero del año
          });
        });
      } else if (renewalType === 'custom_interval' && renewalInterval) {
        // Para intervalos personalizados, crear períodos desde enero
        let intervalMonths = 0;
        if (typeof renewalInterval === 'string') {
          const match = renewalInterval.match(/(\d+)\s*mons?/);
          if (match) {
            intervalMonths = parseInt(match[1]);
          }
        } else if (renewalInterval?.months) {
          intervalMonths = renewalInterval.months;
        }

        if (intervalMonths > 0) {
          // Procesar meses secuencialmente para crear períodos
          let i = 0;
          while (i < months.length) {
            const monthStr = months[i]; // "2024-01", "2024-02", etc.
            const [yearStr, monthNumStr] = monthStr.split('-');
            const year = parseInt(yearStr);
            const monthNum = parseInt(monthNumStr) - 1; // Convertir a 0-11 para el cálculo

            // Calcular el período al que pertenece este mes
            // Para bimestral: 0-1 (ene-feb), 2-3 (mar-abr), 4-5 (may-jun), etc.
            const periodIndex = Math.floor(monthNum / intervalMonths);
            const periodStartMonth = periodIndex * intervalMonths;
            const periodEndMonth = Math.min(periodStartMonth + intervalMonths - 1, 11);

            // Recolectar todos los meses consecutivos del mismo período y año
            let spanEnd = i;
            while (spanEnd < months.length) {
              const nextMonthStr = months[spanEnd];
              const [nextYearStr, nextMonthNumStr] = nextMonthStr.split('-');
              const nextYear = parseInt(nextYearStr);
              const nextMonthNum = parseInt(nextMonthNumStr) - 1; // 0-11

              if (nextYear !== year ||
                  nextMonthNum < periodStartMonth ||
                  nextMonthNum > periodEndMonth) {
                break;
              }
              spanEnd++;
            }

            // Crear el span para este período
            const periodMonths = months.slice(i, spanEnd);
            // Get the best status in the period (approved > pending_approval > rejected > reemplazado > not_uploaded)
            const statusPriority: Record<string, number> = { 'approved': 4, 'pending_approval': 2, 'rejected': 1, 'reemplazado': 0, 'not_uploaded': 0 };
            let bestStatus = docStatus.monthStatuses[periodMonths[0]];
            periodMonths.forEach(month => {
              const monthStatus = docStatus.monthStatuses[month];
              if (statusPriority[monthStatus.status] > statusPriority[bestStatus.status]) {
                bestStatus = monthStatus;
              }
            });
            const periodStatus = bestStatus;

            spans.push({
              startIndex: i,
              span: spanEnd - i,
              status: periodStatus,
              periodStartMonth: `${year}-${String(periodStartMonth + 1).padStart(2, '0')}`,
              periodEndMonth: `${year}-${String(periodEndMonth + 1).padStart(2, '0')}`
            });

            i = spanEnd;
          }
        } else {
          // Intervalo inválido, mostrar todo como celdas separadas
          months.forEach((month, index) => {
            spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
          });
        }
      } else if (renewalType === 'fixed_date') {
        // Para fecha fija, buscar el mes con el documento
        const uploadedMonth = months.findIndex(month =>
          docStatus.monthStatuses[month].status !== 'not_uploaded'
        );
        if (uploadedMonth >= 0) {
          spans.push({
            startIndex: uploadedMonth,
            span: 1,
            status: docStatus.monthStatuses[months[uploadedMonth]]
          });
          // Llenar el resto con celdas vacías
          for (let i = 0; i < months.length; i++) {
            if (i !== uploadedMonth) {
              spans.push({startIndex: i, span: 1, status: docStatus.monthStatuses[months[i]]});
            }
          }
          // Ordenar por startIndex
          spans.sort((a, b) => a.startIndex - b.startIndex);
        } else {
          // Sin documentos cargados, mostrar todo como no cargado
          months.forEach((month, index) => {
            spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
          });
        }
      } else {
        // Default to monthly behavior
        months.forEach((month, index) => {
          spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
        });
      }

      return spans;
    };

    return getCellSpans().map((spanInfo, idx) => {
      const status = spanInfo.status;
      // Para custom_interval, usar el último mes del período, para otros usar el primero
      const clickMonth = (renewalType === 'custom_interval' && spanInfo.periodEndMonth)
        ? spanInfo.periodEndMonth
        : (spanInfo.periodStartMonth || months[spanInfo.startIndex]);

      // Check if this month is in the future or current month
      const [yearStr, monthStr] = clickMonth.split('-');
      const cellYear = parseInt(yearStr);
      const cellMonth = parseInt(monthStr) - 1; // Convert to 0-based month
      const today = new Date();
      const currentActualYear = today.getFullYear();
      const currentActualMonth = today.getMonth();

      // Check if this is current or future month
      const isCurrentOrFutureMonth = cellYear > currentActualYear ||
                                    (cellYear === currentActualYear && cellMonth >= currentActualMonth);

      // Para custom_interval: verificar si estamos en el mes siguiente al período
      let isBlockedCustomInterval = false;
      if (renewalType === 'custom_interval' && renewalInterval && spanInfo.span > 1) {
        // Obtener el primer mes del período
        const firstMonth = months[spanInfo.startIndex];
        const [firstYearStr, firstMonthStr] = firstMonth.split('-');
        const firstCellYear = parseInt(firstYearStr);
        const firstCellMonth = parseInt(firstMonthStr) - 1; // 0-11

        // Extraer el número de meses del intervalo
        let intervalMonths = 0;
        if (typeof renewalInterval === 'string') {
          const match = renewalInterval.match(/(\d+)\s*mons?/);
          if (match) {
            intervalMonths = parseInt(match[1]);
          }
        } else if (renewalInterval?.months) {
          intervalMonths = renewalInterval.months;
        }

        // Bloquear si currentActualMonth < n + intervalMonths (sin importar si hay documento)
        // Ejemplo: Para ene-feb (n=0, interval=2), bloquear si currentMonth < 0+2 (marzo)
        if (!isCurrentOrFutureMonth && intervalMonths > 0) {
          // Calcular si el mes actual está antes del mes de habilitación
          const enableMonth = firstCellMonth + intervalMonths; // mes en que se habilita (0-11)

          // Ajustar si el mes de habilitación cruza al año siguiente
          const enableYear = enableMonth > 11 ? firstCellYear + 1 : firstCellYear;
          const enableMonthAdjusted = enableMonth > 11 ? enableMonth - 12 : enableMonth;

          const isBeforeEnableDate = (currentActualYear < enableYear) ||
                                    (currentActualYear === enableYear && currentActualMonth < enableMonthAdjusted);

          if (isBeforeEnableDate) {
            isBlockedCustomInterval = true;
          }
        }
      }

      const isClickable = !isCurrentOrFutureMonth && !isBlockedCustomInterval;
      const displayStatus = cellDisplayStatus(status.status, status.rejectionReason);
      const titleText = `${docStatus.documentName} · ${clickMonth} · ${STATUS_META[displayStatus].label}${!isClickable ? ' · Periodo no disponible aún' : ''}`;
      const startMonthIndex = parseInt(months[spanInfo.startIndex].split('-')[1]) - 1;
      const endMonthIndex = startMonthIndex + spanInfo.span - 1;
      const shortLabel = spanInfo.span >= 12
        ? 'Año'
        : spanInfo.span > 1
          ? `${MONTH_SHORT[startMonthIndex]}–${MONTH_SHORT[endMonthIndex]}`
          : MONTH_SHORT[startMonthIndex];

      return {
        key: `${clickMonth}-${idx}`,
        span: spanInfo.span,
        clickMonth,
        isClickable,
        status: displayStatus,
        titleText,
        documentId: status.documentId,
        shortLabel,
      };
    });
  };

  const attentionSince = isDevMode && impersonatedUserId ? null : userProfile?.created_at ?? null;
  const attentionItems = useMemo(() => buildAttentionItems({
    definitions: allMonthlyDocumentDefinitions,
    requirements: allRequirementsForTable as Array<{ definition_id: string; documents?: RequirementDocument[] | null }>,
    overrides: userOverrides,
    since: attentionSince,
  }), [allMonthlyDocumentDefinitions, allRequirementsForTable, userOverrides, attentionSince]);


  const toast = <Toast toast={toastMessage} onDismiss={dismissToast} />;

  if (loading) {
    return (
      <PageContainer width="wide">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
          <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white" role="status" aria-label="Cargando documentos">
            {[1, 2, 3].map(i => (
              <div key={i} className="px-5 py-6">
                <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-300 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
        {toast}
      </PageContainer>
    );
  }

  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const sinceKey = attentionSince ? format(new Date(attentionSince), 'yyyy-MM-dd') : null;

  const requirementDeadline = (requirement: Requirement) => documentDeadline({
    dueDate: requirement.due_date,
    applicableMonth: MONTH_CODES[selectedMonth],
    applicableYear: selectedYear,
    renewalType: requirement.definition.renewal_type,
    renewalInterval: requirement.definition.renewal_interval,
  }, 1);

  // Presentation only: the latest version decides the badge; without versions
  // the requirement's own status (or its passed deadline) does.
  const requirementStatus = (requirement: Requirement): DisplayStatus => {
    const latest = latestActiveDocument(requirement.documents as RequirementDocument[] | null);
    if (latest) return documentDisplayStatus(latest);
    if (requirement.status === 'fulfilled') return 'approved';
    const deadline = requirementDeadline(requirement);
    if (deadline && deadline < todayKey && (!sinceKey || deadline >= sinceKey) && !getUploadAvailability(requirement).blocked) return 'overdue';
    return 'missing';
  };

  const statusById = new Map(requirements.map(req => [req.id, requirementStatus(req)]));

  // Filter requirements based on search and filters
  const filteredRequirements = requirements.filter(req => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = req.definition.name.toLowerCase().includes(query);
      const matchesDescription = req.definition.description?.toLowerCase().includes(query) || false;
      const matchesCategory = req.definition.category?.name.toLowerCase().includes(query) || false;
      if (!matchesName && !matchesDescription && !matchesCategory) {
        return false;
      }
    }

    // Status filter
    if (selectedStatus !== 'all') {
      const status = statusById.get(req.id);
      if (selectedStatus === 'missing') {
        if (status !== 'missing' && status !== 'overdue') return false;
      } else if (status !== selectedStatus) {
        return false;
      }
    }

    // Category filter
    if (selectedCategory !== 'all') {
      const categoryName = req.definition.category?.name || 'Sin Categoría';
      if (categoryName !== selectedCategory) return false;
    }

    return true;
  });

  const filtersActive = searchQuery !== '' || selectedStatus !== 'all' || selectedCategory !== 'all';
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedCategory('all');
  };

  // Sort requirements by category name first, then definition name to match table view order
  const sortedRequirements = [...filteredRequirements].sort((a, b) => {
    const categoryA = a.definition.category?.name || 'Sin Categoría';
    const categoryB = b.definition.category?.name || 'Sin Categoría';
    const categoryCompare = categoryA.localeCompare(categoryB);
    if (categoryCompare !== 0) return categoryCompare;
    return a.definition.name.localeCompare(b.definition.name);
  });

  const groupedRequirements = sortedRequirements.reduce((acc, req) => {
    const categoryName = req.definition.category?.name || 'Sin Categoría';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(req);
    return acc;
  }, {} as Record<string, Requirement[]>);

  // Get unique categories for filter dropdown
  const allCategories = Array.from(new Set(requirements.map(req => req.definition.category?.name || 'Sin Categoría')));

  const countStatus = (...statuses: DisplayStatus[]) => requirements.filter(r => statuses.includes(statusById.get(r.id)!)).length;
  const stats: Array<{ label: string; value: number; className: string }> = [
    { label: STATUS_META.missing.label, value: countStatus('missing', 'overdue'), className: 'text-slate-900' },
    { label: STATUS_META.pending_approval.label, value: countStatus('pending_approval'), className: 'text-blue-700' },
    { label: 'Por corregir', value: countStatus('rejected', 'correction_requested'), className: 'text-red-700' },
    { label: STATUS_META.approved.label, value: countStatus('approved'), className: 'text-green-700' },
  ];

  // Current-month uploads open once the month ends.
  const today = new Date();
  const nextPeriodDisabled = new Date(selectedYear, selectedMonth + 1) >= new Date(today.getFullYear(), today.getMonth(), 1);
  const nextPeriodHelp = `${MONTH_NAMES[today.getMonth()]} se podrá cargar a partir del 1 de ${MONTH_NAMES[(today.getMonth() + 1) % 12].toLowerCase()}, cuando termine el mes.`;

  const renderUpload = (requirement: Requirement, replacement = false) => (
    <UploadDropzone
      inputLabel={`Subir ${requirement.definition.name}`}
      buttonText={replacement ? 'Nueva versión' : 'Subir documento'}
      acceptsSua={acceptsSuaFiles(requirement.definition.name)}
      uploading={!!uploadingFiles[requirement.id]}
      error={uploadErrors[requirement.id]}
      onFile={(file) => handleFileUpload(requirement.id, file)}
    />
  );

  const renderUnavailable = (availableFrom: Date | null) => (
    <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600" aria-disabled="true">
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <p>
        {availableFrom
          ? <>Disponible a partir del {format(availableFrom, "d 'de' MMMM 'de' yyyy", { locale: es })}.</>
          : 'Este periodo aún no está abierto para carga.'}
        <span className="mt-0.5 block text-slate-500">La carga se habilita cuando termina el periodo.</span>
      </p>
    </div>
  );

  const cellToneClass = (status: DisplayStatus) =>
    status === 'missing' ? 'text-slate-400' : `${TONE_CLASSES[STATUS_META[status].tone]}`;

  const LEGEND: DisplayStatus[] = ['approved', 'pending_approval', 'correction_requested', 'rejected', 'expired', 'missing'];

  const supplierDisplayName = formatPersonName(
    isDevMode && impersonatedUserId
      ? availableUsers.find(u => u.id === impersonatedUserId)?.full_name || availableUsers.find(u => u.id === impersonatedUserId)?.email
      : userProfile?.full_name || userProfile?.email
  );

  const viewToggle = (
    <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
      <button
        aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
          viewMode === 'list'
            ? 'bg-slate-100 text-slate-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        <FileText className="w-4 h-4" aria-hidden="true" />
        Lista
      </button>
      <button
        aria-pressed={viewMode === 'table'} onClick={() => setViewMode('table')}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
          viewMode === 'table'
            ? 'bg-slate-100 text-slate-900'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        <Calendar className="w-4 h-4" aria-hidden="true" />
        Tabla anual
      </button>
    </div>
  );

  const noAssignments = (
    <div className="rounded-lg border border-slate-200 bg-white px-6 py-12 text-center">
      <FileText className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden="true" />
      <h3 className="mb-2 text-sm font-medium text-slate-900">Sin documentos asignados</h3>
      <p className="text-sm text-gray-600">Aún no tienes documentos asignados. Contacta a tu administrador.</p>
    </div>
  );

  return (
    <PageContainer width="wide">
      {/* Dev Mode Menu */}
      {isDevMode && (
        <div className="rounded-lg bg-yellow-50 border-2 border-yellow-400 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded">
                MODO DESARROLLO
              </span>
              <button
                onClick={() => setShowDevMenu(!showDevMenu)}
                className="text-sm text-yellow-800 hover:text-yellow-900 underline"
              >
                {showDevMenu ? 'Ocultar' : 'Mostrar'} suplantación
              </button>
            </div>

            {showDevMenu && (
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <select
                  value={impersonatedUserId}
                  onChange={(e) => setImpersonatedUserId(e.target.value)}
                  aria-label="Usuario a suplantar"
                  className="max-w-full px-3 py-1 border border-yellow-400 rounded text-sm"
                >
                  <option value="">-- Usar mi propia cuenta --</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.full_name || u.email} ({u.email})
                    </option>
                  ))}
                </select>
                {impersonatedUserId && (
                  <span className="text-sm text-yellow-800">
                    Suplantando: <code className="bg-yellow-200 px-2 py-1 rounded">
                      {availableUsers.find(u => u.id === impersonatedUserId)?.email || impersonatedUserId}
                    </code>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <PageHeader
        title="Mis documentos"
        description={supplierDisplayName ? `${supplierDisplayName} · Entrega y seguimiento de tus documentos` : 'Entrega y seguimiento de tus documentos'}
        actions={viewToggle}
      />

      <AttentionPanel
        items={attentionItems}
        onOpen={(item) => handleTableCellClick(item.targetMonth, item.definitionId, item.documentId)}
      />

      {/* Period navigation */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 sm:px-5">
          <h2 className="text-xl font-semibold text-gray-900">
            {viewMode === 'table'
              ? `${new Date().getFullYear() + calendarOffset}`
              : `${MONTH_NAMES[selectedMonth]} ${selectedYear}`}
          </h2>

          <div className="flex items-center gap-1">
            {viewMode === 'list' ? (
              <>
                <button
                  onClick={() => {
                    const newDate = new Date(selectedYear, selectedMonth - 1);
                    const minDate = new Date(2015, 0); // January 2015

                    if (newDate >= minDate) {
                      setSelectedMonth(newDate.getMonth());
                      setSelectedYear(newDate.getFullYear());
                    }
                  }}
                  disabled={(() => {
                    const newDate = new Date(selectedYear, selectedMonth - 1);
                    const minDate = new Date(2015, 0);
                    return newDate < minDate;
                  })()}
                  aria-label="Periodo anterior" className="rounded-md p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                </button>

                <button
                  onClick={() => {
                    const newDate = new Date(selectedYear, selectedMonth + 1);
                    const today = new Date();
                    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

                    if (newDate < currentMonthStart) {
                      setSelectedMonth(newDate.getMonth());
                      setSelectedYear(newDate.getFullYear());
                    }
                  }}
                  disabled={nextPeriodDisabled}
                  title={nextPeriodDisabled ? nextPeriodHelp : undefined}
                  aria-describedby={nextPeriodDisabled ? 'next-period-help' : undefined}
                  aria-label="Periodo siguiente" className="rounded-md p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                >
                  <ArrowLeft className="w-4 h-4 rotate-180" aria-hidden="true" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    const currentYear = new Date().getFullYear();
                    const targetYear = currentYear + calendarOffset - 1;
                    if (targetYear >= 2015) {
                      setCalendarOffset(calendarOffset - 1);
                    }
                  }}
                  disabled={(() => {
                    const currentYear = new Date().getFullYear();
                    const targetYear = currentYear + calendarOffset - 1;
                    return targetYear < 2015;
                  })()}
                  aria-label="Año anterior" className="rounded-md p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                </button>

                <button
                  onClick={() => setCalendarOffset(calendarOffset + 1)}
                  aria-label="Año siguiente" className="rounded-md p-2 text-slate-500 hover:bg-slate-100"
                >
                  <ArrowLeft className="w-4 h-4 rotate-180" aria-hidden="true" />
                </button>
              </>
            )}
          </div>

          {viewMode === 'list' && nextPeriodDisabled && (
            <p id="next-period-help" className="w-full text-xs text-slate-500 sm:w-auto">{nextPeriodHelp}</p>
          )}
        </div>

        {viewMode === 'list' && requirements.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-200 px-4 py-3 text-sm text-slate-500 sm:px-5">
            <span><strong className="font-semibold text-slate-900">{requirements.length}</strong> documentos</span>
            {stats.map(stat => (
              <span key={stat.label}>{stat.label}: <strong className={`font-semibold ${stat.className}`}>{stat.value}</strong></span>
            ))}
          </div>
        )}
      </div>

      {/* Filters - Only show in list view */}
      {viewMode === 'list' && requirements.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_200px_200px]">
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" aria-hidden="true" />
            <input
              type="text"
              aria-label="Buscar documentos" placeholder="Buscar documento"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="min-w-0">
            <select
              aria-label="Estado" value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los estados</option>
              {STATUS_FILTERS.map(status => (
                <option key={status} value={status}>{STATUS_META[status].label}</option>
              ))}
            </select>
          </div>

          <select
            aria-label="Categoría" value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todas las categorías</option>
            {allCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      )}

      {/* Monthly Table View */}
      {viewMode === 'table' && (() => {
        const { monthlyStatuses } = generateMonthlyTableData();
        if (monthlyStatuses.length === 0) return noAssignments;

        const tableRows = monthlyStatuses.map(docStatus => ({ docStatus, cells: buildAnnualCells(docStatus) }));
        const byCategory: Array<{ category: string; rows: typeof tableRows }> = [];
        tableRows.forEach(row => {
          const last = byCategory[byCategory.length - 1];
          if (last && last.category === row.docStatus.categoryName) last.rows.push(row);
          else byCategory.push({ category: row.docStatus.categoryName, rows: [row] });
        });
        const headerMonths = Object.keys(monthlyStatuses[0].monthStatuses).sort();

        const legend = (
          <ul aria-label="Leyenda de estados" className="flex flex-wrap gap-x-5 gap-y-2 border-b border-slate-200 px-4 py-3 text-xs text-slate-600">
            {LEGEND.map(status => (
              <li key={status} className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className={`inline-flex h-5 w-5 items-center justify-center rounded font-medium ${cellToneClass(status)}`}>{STATUS_SYMBOL[status]}</span>
                {STATUS_META[status].label}
              </li>
            ))}
          </ul>
        );

        if (!isSmUp) {
          return (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              {legend}
              {byCategory.map(group => (
                <section key={group.category} aria-label={group.category}>
                  <h3 className="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">{group.category}</h3>
                  <ul className="divide-y divide-slate-100">
                    {group.rows.map(({ docStatus, cells }) => (
                      <li key={docStatus.requirementId} className="px-4 py-3">
                        <p className="mb-2 text-sm font-medium text-slate-800">{docStatus.documentName}</p>
                        <ul className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1" aria-label={`Periodos de ${docStatus.documentName}`}>
                          {cells.map(cell => (
                            <li key={cell.key} className="shrink-0">
                              <button type="button" disabled={!cell.isClickable} aria-label={cell.titleText} title={cell.titleText}
                                onClick={() => handleTableCellClick(cell.clickMonth, docStatus.definitionId, cell.documentId)}
                                className={`flex min-h-[44px] min-w-[44px] flex-col items-center justify-center rounded-md px-2 text-xs ring-1 ring-inset ring-slate-200 disabled:cursor-not-allowed disabled:opacity-40 ${cellToneClass(cell.status)}`}>
                                <span className="text-[11px] text-slate-500">{cell.shortLabel}</span>
                                <span aria-hidden="true" className="font-medium">{STATUS_SYMBOL[cell.status]}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          );
        }

        return (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {legend}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] table-fixed text-sm">
                <colgroup>
                  <col style={{ width: 288 }} />
                  {headerMonths.map(month => <col key={month} />)}
                </colgroup>
                <thead>
                  <tr className="border-b">
                    <th className="sticky left-0 z-10 bg-white text-left py-3 px-4 font-medium text-slate-500 min-w-[240px] w-64">Documento</th>
                    {headerMonths.map((month, i) => {
                      const [year, monthNum] = month.split('-');
                      const date = new Date(parseInt(year), parseInt(monthNum) - 1);

                      // Check if month is disabled (current or future)
                      const today = new Date();
                      const currentActualYear = today.getFullYear();
                      const currentActualMonth = today.getMonth();
                      const cellYear = parseInt(year);
                      const cellMonth = parseInt(monthNum) - 1;
                      const isDisabledMonth = cellYear > currentActualYear ||
                                            (cellYear === currentActualYear && cellMonth >= currentActualMonth);

                      return (
                        <th key={i} scope="col" className="min-w-[52px] px-1 py-2 text-center text-xs font-medium text-slate-500">
                          <button disabled={isDisabledMonth} aria-label={`Abrir ${format(date, 'MMMM yyyy', { locale: es })}`}
                            title={isDisabledMonth ? 'Este mes aún no está abierto para carga' : undefined}
                            onClick={() => handleTableCellClick(month)}
                            className="rounded px-2 py-2 capitalize hover:bg-slate-100 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500">
                            {format(date, 'MMM', { locale: es })}
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {byCategory.map(group => [
                    <tr key={`category-${group.category}`} className="bg-gray-50">
                      <td colSpan={13} className="py-2 px-3 font-semibold text-gray-700 w-full">
                        {group.category}
                      </td>
                    </tr>,
                    ...group.rows.map(({ docStatus, cells }) => (
                      <tr key={docStatus.requirementId} className="border-b hover:bg-gray-50">
                        <td className="sticky left-0 z-10 bg-white py-3 px-4 font-medium text-sm text-slate-700">{docStatus.documentName}</td>
                        {cells.map(cell => (
                          <td
                            key={cell.key}
                            colSpan={cell.span}
                            className={`p-1 text-center ${cell.span > 1 ? 'border-x border-slate-200' : ''}`}
                          >
                            <button type="button" disabled={!cell.isClickable} title={cell.titleText} aria-label={cell.titleText}
                              onClick={() => handleTableCellClick(cell.clickMonth, docStatus.definitionId, cell.documentId)}
                              className={`min-h-[36px] w-full rounded-md px-2 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 hover:ring-1 hover:ring-slate-300 disabled:opacity-40 disabled:cursor-not-allowed ${cellToneClass(cell.status)}`}>
                              <span aria-hidden="true">{STATUS_SYMBOL[cell.status]}</span>
                            </button>
                          </td>
                        ))}
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Requirements List */}
      {viewMode === 'list' && requirements.length === 0 && noAssignments}

      {viewMode === 'list' && requirements.length > 0 && (
        <div className="space-y-5">
          {Object.entries(groupedRequirements).map(([categoryName, categoryRequirements]) => (
            <div key={categoryName} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 sm:px-5 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">{categoryName}</h3>
            </div>

            <div className="divide-y divide-slate-200">
              {categoryRequirements.map((requirement) => {
                const status = statusById.get(requirement.id) || 'missing';
                const latest = latestActiveDocument(requirement.documents as RequirementDocument[] | null);
                const reason = reviewerReason(latest);
                const deadlineChip = getDeadlineChip(
                  requirementDeadline(requirement),
                  status === 'approved' ? 'approved' : status === 'pending_approval' ? 'delivered' : 'open',
                );
                const hasDocuments = !!requirement.documents && requirement.documents.length > 0;

                // Allow upload unless the most recent active document is approved
                let action: React.ReactNode = null;
                if (hasDocuments) {
                  if (!latest || latest.status !== 'approved') action = renderUpload(requirement, true);
                } else {
                  const availability = getUploadAvailability(requirement);
                  action = availability.blocked ? renderUnavailable(availability.availableFrom) : renderUpload(requirement);
                }

                return (
                <div
                  key={requirement.id}
                  id={`requirement-${requirement.id}`}
                  className="space-y-3 p-4 sm:px-5"
                >
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_170px_240px] md:items-start md:gap-x-5">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-slate-900">
                          {requirement.definition.name}
                        </h4>
                        {/* Etiqueta is_moral */}
                        {requirement.definition.is_moral === true && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Exclusivo personas morales
                          </span>
                        )}
                        {requirement.definition.is_moral === false && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Exclusivo personas físicas
                          </span>
                        )}
                        {(() => {
                          const override = userOverrides.find(o => o.definition_id === requirement.definition_id);
                          return override ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                              {getOverrideLabel(override)}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      {requirement.definition.description && (
                        <details className="mb-2 text-xs text-slate-500">
                          <summary className="w-fit cursor-pointer hover:text-slate-900">Requisitos</summary>
                          <p className="mt-2 max-w-3xl text-sm">{requirement.definition.description}</p>
                        </details>
                      )}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        {deadlineChip && (
                          <span title={deadlineChip.title} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ring-1 ring-inset ${TONE_CLASSES[deadlineChip.tone]}`}>
                            <Clock className="h-3 w-3" aria-hidden="true" />
                            {deadlineChip.label}
                            <span className="sr-only"> ({deadlineChip.title})</span>
                          </span>
                        )}
                        <span>{({ monthly: 'Mensual', yearly: 'Anual', none: 'Único', custom_interval: 'Periódico' } as Record<string, string>)[requirement.definition.renewal_type] || 'Periódico'}</span>
                      </div>
                      {reason && (
                        <div role="note" className={`mt-3 rounded-md border px-3 py-2 text-sm ${status === 'rejected' ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                          <p><strong className="font-semibold">Motivo del revisor:</strong> {reason}</p>
                          {action && <p className="mt-1 text-xs opacity-80">Corrige lo indicado y sube una nueva versión.</p>}
                        </div>
                      )}
                    </div>

                    <div className="flex min-w-0 items-center md:justify-center">
                      <span className={statusBadgeClass(status)}>{STATUS_META[status].label}</span>
                    </div>

                    <div className="min-w-0">{action}</div>
                  </div>

                  {/* Documents */}
                  {hasDocuments && (
                    <div className="min-w-0 space-y-2">
                      {requirement.documents!.map((doc) => {
                        const docStatus = documentDisplayStatus(doc);
                        const shownAbove = !!reason && latest?.id === doc.id;
                        const message = fileMessages[doc.id];
                        const isPdf = doc.file_location.toLowerCase().endsWith('.pdf');
                        return (
                          <div key={doc.id} id={`document-${doc.id}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-3 py-2 rounded-md">
                              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-500" aria-hidden="true" />
                                <span className="text-sm text-slate-700 break-all">{doc.original_filename}</span>
                                <span className={statusBadgeClass(docStatus)}>{STATUS_META[docStatus].label}</span>
                                {(doc as RequirementDocument).is_deleted && (
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Eliminado</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  {format(new Date(doc.upload_date), 'dd/MM/yyyy')}
                                </span>
                                {isPdf ? (
                                  <button
                                    type="button"
                                    onClick={() => handlePreview(doc)}
                                    className="rounded p-1 text-blue-600 hover:text-blue-800 focus-visible:ring-2 focus-visible:ring-blue-500"
                                    title="Vista previa"
                                    aria-label={`Vista previa de ${doc.original_filename}`}
                                  >
                                    <Eye className="w-4 h-4" aria-hidden="true" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenFile(doc)}
                                    className="rounded p-1 text-blue-600 hover:text-blue-800 focus-visible:ring-2 focus-visible:ring-blue-500"
                                    title="Abrir en pestaña nueva"
                                    aria-label={`Abrir ${doc.original_filename} en pestaña nueva`}
                                  >
                                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {message && (
                              <p role={message.tone === 'error' ? 'alert' : 'status'} className={`mt-1 text-xs ${message.tone === 'error' ? 'text-red-700' : 'text-slate-600'}`}>
                                {message.text}{' '}
                                {message.url && <a href={message.url} target="_blank" rel="noreferrer" className="font-medium text-blue-700 underline">Abrir archivo</a>}
                              </p>
                            )}

                            {/* Show rejection reason immediately after this document if rejected */}
                            {!shownAbove && doc.status === 'rejected' && doc.rejection_reason && (
                              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-800">
                                  <strong>Motivo de rechazo:</strong> {doc.rejection_reason}
                                </p>
                              </div>
                            )}

                            {/* Show revision request immediately after this document if pending_approval with rejection_reason */}
                            {!shownAbove && doc.status === 'pending_approval' && doc.rejection_reason?.startsWith(REVISION_PREFIX) && (
                              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-sm text-amber-800">
                                  <strong>Solicitud de revisión:</strong> {doc.rejection_reason.replace(REVISION_PREFIX, '').trim()}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
          ))}

          {filteredRequirements.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white px-6 py-12 text-center">
              <Search className="w-10 h-10 text-gray-400 mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-sm font-medium text-slate-900 mb-2">Ningún documento coincide con los filtros</h3>
              <p className="text-sm text-gray-600">Prueba con otra búsqueda, estado o categoría.</p>
              {filtersActive && (
                <button type="button" onClick={clearFilters} className="mt-4 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-500">
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfUrl && preview && (
        <PreviewDialog url={pdfUrl} fileName={preview.name} onClose={closePreview} />
      )}
      {toast}
    </PageContainer>
  );
}
